import React, { useState } from 'react';
import axios from 'axios';
import { appLogger, ErrorCategory } from '../utils/errorLogger';

const ErrorLogTester: React.FC = () => {
  const [testResult, setTestResult] = useState<string>('');
  
  // Test 1: Manual error logging
  const testManualErrorLog = () => {
    try {
      appLogger.error('Test manual error log', {
        source: 'error-test-component',
        category: ErrorCategory.CLIENT,
        metadata: { testId: 'manual-error-log' }
      });
      setTestResult('Manual error log sent successfully');
    } catch (error) {
      setTestResult(`Failed to send manual error log: ${error}`);
    }
  };
  
  // Test 2: Trigger a React rendering error
  const testRenderError = () => {
    // This will cause a React error that should be caught by ErrorBoundary
    throw new Error('Test React rendering error');
  };
  
  // Test 3: Trigger an API error
  const testApiError = async () => {
    try {
      // This should trigger an Axios error that will be caught by the interceptor
      await axios.get('/api/non-existent-endpoint');
      setTestResult('API call completed (unexpected)');
    } catch (error) {
      setTestResult('API error triggered successfully (expected)');
    }
  };
  
  // Test 4: Trigger an unhandled promise rejection
  const testUnhandledRejection = () => {
    // This creates an unhandled promise rejection
    new Promise((_, reject) => {
      reject(new Error('Test unhandled promise rejection'));
    });
    setTestResult('Unhandled promise rejection triggered');
  };
  
  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4">Error Logging Test Panel</h2>
      <div className="space-y-4">
        <div>
          <button
            onClick={testManualErrorLog}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 mr-2"
          >
            Test Manual Error Log
          </button>
          <p className="text-sm text-gray-600 mt-1">
            Tests direct error logging through the errorLogger utility
          </p>
        </div>
        
        <div>
          <button
            onClick={() => {
              try {
                testRenderError();
              } catch (e) {
                setTestResult('React error caught locally (should also be caught by ErrorBoundary)');
              }
            }}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 mr-2"
          >
            Test React Error
          </button>
          <p className="text-sm text-gray-600 mt-1">
            Tests ErrorBoundary by triggering a rendering error
          </p>
        </div>
        
        <div>
          <button
            onClick={testApiError}
            className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 mr-2"
          >
            Test API Error
          </button>
          <p className="text-sm text-gray-600 mt-1">
            Tests Axios interceptor by making a request to a non-existent endpoint
          </p>
        </div>
        
        <div>
          <button
            onClick={testUnhandledRejection}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 mr-2"
          >
            Test Unhandled Rejection
          </button>
          <p className="text-sm text-gray-600 mt-1">
            Tests global unhandled promise rejection handler
          </p>
        </div>
        
        {testResult && (
          <div className="mt-4 p-3 bg-gray-100 rounded">
            <h3 className="font-medium">Test Result:</h3>
            <p>{testResult}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorLogTester;
